import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import {
  buttonBase,
  buttonSizes,
  buttonVariants,
  type ButtonSize,
  type ButtonVariant,
} from './buttonStyles';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render content before the label (e.g. an icon) */
  leadingIcon?: ReactNode;
  /** Render content after the label */
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

/**
 * Button — the primary CTA primitive across the site.
 * For <a> or <Link> rendering, use `buttonClasses(variant, size)` from
 * ./buttonStyles instead of wrapping this component.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary-purple',
    size = 'md',
    leadingIcon,
    trailingIcon,
    fullWidth,
    className,
    type = 'button',
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        buttonBase,
        buttonVariants[variant],
        buttonSizes[size],
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {leadingIcon && <span aria-hidden="true" className="inline-flex">{leadingIcon}</span>}
      {children}
      {trailingIcon && <span aria-hidden="true" className="inline-flex">{trailingIcon}</span>}
    </button>
  );
});
